import { useState, useEffect, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import { useI18n } from '../context/I18nContext';
import { navigate } from '../shared/lib/router';

const COLLAPSE_STORAGE_KEY = 'tgmsaas.sidebar.collapsed';

/**
 * Collapsible left navigation. Expanded shows icon + label; collapsed shows
 * icon-only with a title-attribute tooltip. Collapse state persists across
 * reloads via localStorage. Items come from modules/registry.js, filtered
 * by the enabledModules/permissions passed in from AuthContext - this
 * component does no data fetching of its own.
 */
export default function Sidebar({ modules, activeModuleKey, enabledModules, permissions }) {
  const { t } = useI18n();
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_STORAGE_KEY, collapsed ? '1' : '0');
    } catch (e) {
      // ignore storage errors
    }
  }, [collapsed]);

  const toggleCollapsed = useCallback(() => setCollapsed((prev) => !prev), []);

  const visibleModules = modules.filter((mod) => {
    const moduleOn = enabledModules.includes(mod.key);
    const permissionOk = !mod.requiredPermission || permissions.includes(mod.requiredPermission);
    return moduleOn && permissionOk;
  });

  return (
    <aside className={`app-sidebar${collapsed ? ' app-sidebar--collapsed' : ''}`}>
      <div className="app-sidebar__header">
        <button
          type="button"
          className="app-sidebar__toggle"
          onClick={toggleCollapsed}
          title={t('common', collapsed ? 'sidebar.expand' : 'sidebar.collapse')}
          aria-label={t('common', collapsed ? 'sidebar.expand' : 'sidebar.collapse')}
        >
          {collapsed ? <Menu size={20} aria-hidden="true" /> : <X size={20} aria-hidden="true" />}
        </button>
      </div>
      <nav className="app-sidebar__nav" aria-label={t('common', 'nav.primary')}>
        <ul className="app-sidebar__list">
          {visibleModules.map((mod) => {
            const Icon = mod.icon;
            const label = t(mod.navLabelKey[0], mod.navLabelKey[1]);
            const isActive = mod.key === activeModuleKey;
            return (
              <li key={mod.path} className="app-sidebar__item">
                <a
                  href={mod.path}
                  className={`app-sidebar__link${isActive ? ' app-sidebar__link--active' : ''}`}
                  title={collapsed ? label : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(mod.path);
                  }}
                >
                  <Icon size={20} aria-hidden="true" />
                  {!collapsed && <span className="app-sidebar__label">{label}</span>}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
